import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Share2, Link2, Check, Facebook } from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

// WhatsApp / X (Twitter) SVG icons (Lucide doesn't ship social brand icons)
const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M20.52 3.48A11.94 11.94 0 0 0 12.06 0C5.5 0 .17 5.32.17 11.88c0 2.09.55 4.13 1.6 5.94L0 24l6.34-1.66a11.9 11.9 0 0 0 5.71 1.46h.01c6.55 0 11.88-5.33 11.88-11.88a11.83 11.83 0 0 0-3.42-8.44zM12.07 21.8h-.01a9.9 9.9 0 0 1-5.05-1.38l-.36-.22-3.76.99 1-3.66-.23-.38a9.87 9.87 0 0 1-1.52-5.27c0-5.46 4.45-9.9 9.92-9.9 2.65 0 5.14 1.03 7.02 2.91a9.85 9.85 0 0 1 2.9 7.01c0 5.46-4.44 9.9-9.91 9.9zm5.44-7.4c-.3-.15-1.77-.87-2.04-.97-.28-.1-.48-.15-.67.15-.2.3-.77.97-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.47 0 1.46 1.06 2.86 1.21 3.06.15.2 2.1 3.2 5.09 4.49.71.31 1.27.5 1.7.64.71.23 1.36.19 1.87.12.57-.09 1.77-.72 2.02-1.42.25-.7.25-1.29.17-1.42-.07-.12-.27-.2-.57-.35z"/>
  </svg>
);
const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

export default function ShareRow({ item }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const url = typeof window !== "undefined" ? window.location.href : "";
  const title = `${item.title} · ${Number(item.price).toFixed(2)} € · VintedStyle.be`;
  const text = `${t("share.text_prefix")} ${item.title} — ${Number(item.price).toFixed(2)} € 🎀`;

  const targets = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: <WhatsAppIcon className="w-4 h-4" />,
      href: `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`,
      bg: "hover:bg-[#25D366]/10 hover:text-[#25D366]",
    },
    {
      key: "x",
      label: "X",
      icon: <XIcon className="w-4 h-4" />,
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      bg: "hover:bg-foreground/10 hover:text-foreground",
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: <Facebook className="w-4 h-4" />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      bg: "hover:bg-[#1877F2]/10 hover:text-[#1877F2]",
    },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t("share.copied"));
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Copy failed"); }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, text, url }); } catch {}
    } else {
      copy();
    }
  };

  return (
    <div className="mt-10 pt-6 border-t border-border" data-testid="share-row">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="overline text-muted-foreground">{t("share.title")}</div>
        <div className="flex items-center gap-2">
          {targets.map((tg) => (
            <a
              key={tg.key}
              href={tg.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("share_click", { network: tg.key, listing_id: item.id })}
              data-testid={`share-${tg.key}`}
              aria-label={`Share on ${tg.label}`}
              className={`w-10 h-10 rounded-full border border-border flex items-center justify-center transition-colors ${tg.bg}`}
            >
              {tg.icon}
            </a>
          ))}
          <button
            onClick={copy}
            data-testid="share-copy"
            aria-label="Copy link"
            className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-primary/10 hover:text-primary transition-colors"
          >
            {copied ? <Check className="w-4 h-4 text-primary" /> : <Link2 className="w-4 h-4" />}
          </button>
          <button
            onClick={nativeShare}
            data-testid="share-native"
            aria-label="Share"
            className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-primary/10 hover:text-primary transition-colors sm:hidden"
          >
            <Share2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
